import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles: string[];
}

/**
 * ProtectedRoute component - Restricts access based on user authentication and role
 *
 * Usage:
 * <ProtectedRoute allowedRoles={['staff']}>
 *   <StaffDashboard />
 * </ProtectedRoute>
 */
export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, loading } = useAuth();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to appropriate dashboard if user doesn't have required role
  if (!allowedRoles.includes(user.role)) {
    // Redirect based on user's actual role
    switch (user.role) {
      case "customer":
        return <Navigate to="/customer/dashboard" replace />;
      case "staff":
        return <Navigate to="/staff/dashboard" replace />;
      case "merchant":
        return <Navigate to="/merchant/dashboard" replace />;
      case "admin":
        return <Navigate to="/admin/dashboard" replace />;
      default:
        return <Navigate to="/login" replace />;
    }
  }

  // User is authenticated and has the correct role
  return <>{children}</>;
}
